import { Timestamp } from "firebase/firestore";
import { BATCH_SIZE, EMPTY_STRING, PROCESSING, TOTAL_TASKS } from "../constants";
import CollectionService from "./collectionService";
import JobService from "./jobService";
import LlmService from "./llmService";
import StorageService from "./storageService";
import TransformationService from "./transformationService";

export const validFileType = (fileType: string) => {
  return ['image/png', 'image/jpeg', 'image/jpg', 'image/heic', 'application/zip'].includes(fileType);
};

export const handleZipFile = (file: File) => {
  // TODO: handle zip file
};

export const trimText = (text: string, maxLength: number) => {
  if (text.length > maxLength) {
    return text.substring(0, maxLength) + '...';
  }
  return text;
};

export const createCollectionAndTransformation = async (title: string, files: File[], checkedCriteria: boolean[], customCriteria: CustomCriteria[], currentThreadId: string, userId: string) => {
  const presetsPrompt: boolean = !customCriteria || customCriteria?.length === 0;
  const newCollection: PhotoCollection = {
    id: EMPTY_STRING,
    title: title.trim(),
    ownerId: userId,
    totalPhotos: files.length,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  }
  const newCollectionId: string = await CollectionService.addCollection(newCollection);
  const newTransformation: Transformation = {
    id: EMPTY_STRING,
    collectionId: newCollectionId,
    accountId: userId,
    ...(presetsPrompt && { selectionCriteria: {
      ...(checkedCriteria[0] && { blinkFree: true }),
      ...(checkedCriteria[1] && { noBlurry: true }),
      ...(checkedCriteria[2] && { goodComposition: true }),
      ...(checkedCriteria[3] && { noTechnicalIssues: true })
    }}),
    ...(!presetsPrompt && { customCriteria }),
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  }
  const newTransformationId: string = await TransformationService.addTransformation(newTransformation);
  if (!presetsPrompt && currentThreadId && newTransformationId) {
    await LlmService.updateWithTransformationId(currentThreadId, newTransformationId);
  }
  return { newCollectionId, newTransformationId, presetsPrompt };
}

interface UploadFilesAddJobParams {
  files: File[];
  collectionId: string;
  transformationId: string;
  isPresetPrompts: boolean;
  fileTypesSet: Set<string>;
  setUploadProgress: (progress: number) => void;
}
export const uploadFilesAddJob = async ({ files, collectionId, transformationId, isPresetPrompts, fileTypesSet, setUploadProgress }: UploadFilesAddJobParams) => {
  try {
    await StorageService.uploadImagesInBulk(
      files,
      collectionId,
      BATCH_SIZE,
      (completed, total) => {
        const progress = Math.round((completed / total) * 100);
        setUploadProgress(progress);
      }
    );
    const newJob: Job = {
      id: EMPTY_STRING,
      collectionId: collectionId,
      transformationId: transformationId,
      isPresetPrompts: isPresetPrompts,
      status: PROCESSING,
      fileTypes: Array.from(fileTypesSet),
      totalFiles: files?.length,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    }
    const jobId = await JobService.addJob(newJob);
    return jobId;
  } catch (error) {
    console.error('Upload failed:', error);
  }
  return null;
}

export const getSteps = () => [
  {
    index: "1",
    title: "Upload your photos",
    content: "Cureyta analyzes blur, composition, and visual similarity to curate to your photos and streamline your culling process."
  },
  {
    index: "2",
    title: "Generate custom criteria",
    content: "Looking for something specific? Generate custom criteria using ‘Cureyta Chat’ and discover the perfect shot. "
  },
  {
    index: "3",
    title: "View curated images",
    content: "View a gallery of your curated images ready to use or share."
  },
  {
    index: "4",
    title: "Export & edit",
    content: "Seamlessly export your favourite photos into your photo editing program of choice, with a click of a button. "
  }
];

export const getTaskDistribution = (photoIds: string[], displayedPhotos: Photo[]) => {
  const selectedPhotoSet: Set<string> = new Set(photoIds);
  const tasksDistributionMap: {
    [taskIndex: string]: {
      completed?: boolean;
      photos: {
        [photoName: string]: string
      }
    };
  } = {};
  const filesPerTask: number = Math.ceil(photoIds?.length / TOTAL_TASKS);
  const selectedPhotos = displayedPhotos?.filter(photo => selectedPhotoSet.has(photo?.id));
  let fileIndex = 0;
  selectedPhotos?.forEach((photo, index) => {
    const taskIndex = Math.floor(index / filesPerTask).toString();
    if (!tasksDistributionMap[taskIndex]) {
      tasksDistributionMap[taskIndex] = { photos: {} };
    }
    tasksDistributionMap[taskIndex].photos[photo.id] = photo.path;
    fileIndex++;
    if (photo?.jpgPath) {
      tasksDistributionMap[taskIndex].photos[`${photo.id}_jpg`] = photo.jpgPath;
      fileIndex++;
    }
  });
  return { tasksDistributionMap, selectedPhotoSet };
}
